import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { type LoginResult, type MFADetails } from '../types/auth';
import { useAuth } from '../utils/authcontextt';

const UNEXPECTED_ERROR = 'Något gick fel. Försök igen.';

type MfaSetupDetails = NonNullable<LoginResult['mfa_setup']>;

// Ett väntande steg, inte två booleans: inloggningen kan aldrig vara mitt i
// både verifiering och uppsättning, och det ska inte gå att uttrycka heller.
export type PendingChallenge =
  | { stage: 'mfa'; mfa: MFADetails }
  | { stage: 'mfa_setup'; setup: MfaSetupDetails }
  | null;

export type LoginStage = 'credentials' | 'mfa' | 'mfa_setup';

export const useLoginFlow = () => {
  const navigate = useNavigate();
  const { user, login, verifyMFA, activateMFA, loading } = useAuth();
  const [pending, setPending] = useState<PendingChallenge>(null);
  const [error, setError] = useState('');
  const [isLogging, setIsLogging] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const submitCredentials = async (email: string, password: string) => {
    setError('');
    setIsLogging(true);

    try {
      const result = await login(email, password);

      if (result.mfaRequired && result.mfa) {
        setPending({ stage: 'mfa', mfa: result.mfa });
      } else if (result.mfaSetupRequired && result.mfa_setup) {
        setPending({ stage: 'mfa_setup', setup: result.mfa_setup });
      } else if (!result.success) {
        setError(result.error || 'Inloggningen misslyckades');
      }
    } catch {
      setError(UNEXPECTED_ERROR);
    } finally {
      setIsLogging(false);
    }
  };

  // Verifiering och aktivering skiljer sig bara på vilket anrop som görs —
  // steget avgör det, resten av turen är densamma.
  const submitCode = async (code: string) => {
    if (!pending) return;

    setError('');
    setIsVerifying(true);

    try {
      const result =
        pending.stage === 'mfa'
          ? await verifyMFA({ ...pending.mfa, code })
          : await activateMFA({ factorId: pending.setup.factorId, code });

      if (result.success) {
        setPending(null);
      } else {
        setError(result.error || 'Fel kod');
      }
    } catch {
      setError(UNEXPECTED_ERROR);
    } finally {
      setIsVerifying(false);
    }
  };

  return {
    stage: (pending?.stage ?? 'credentials') as LoginStage,
    pending,
    error,
    clearError: () => setError(''),
    isAuthLoading: loading,
    isLogging,
    isVerifying,
    submitCredentials,
    submitCode,
    backToCredentials: () => {
      setPending(null);
      setError('');
    },
  };
};
